import clsx from 'clsx';
import React from 'react';

import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { focusRingClassName } from '#/shared/utils/domHelper';

import { FormFieldSupportingText } from './FormFieldSupportingText';

type MultiTextInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  errorMessage?: string;
  errorLeadingIconName?: string;
  onRemove?: () => void;
  canRemove?: boolean;
};

export const MultiTextInput = React.forwardRef<HTMLInputElement, MultiTextInputProps>(
  ({ errorMessage, errorLeadingIconName, onRemove, canRemove = false, className, ...props }, ref) => {
    const id = React.useId();
    const inputId = `field-${id}`;
    const errorId = `error-${id}`;

    const { 'aria-describedby': originalAriaDescribedBy, ...restProps } = props;

    const ariaDescribedBy = React.useMemo(
      () => [originalAriaDescribedBy, errorMessage ? errorId : undefined].filter(Boolean).join(' ') || undefined,
      [originalAriaDescribedBy, errorMessage, errorId],
    );

    return (
      <div className={clsx('relative w-full', className)}>
        <input
          ref={ref}
          className={clsx(
            'w-full px-16 border-1 rounded-xs bg-surface-l0',
            canRemove ? 'py-16 pr-48' : 'py-16',
            errorMessage ? 'border-error focus:ring-error' : 'border-outline-l4 focus:ring-primary',
            focusRingClassName,
          )}
          id={inputId}
          aria-describedby={ariaDescribedBy}
          {...restProps}
        />

        {/* Delete Button */}
        {canRemove && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute right-16 top-[28px] -translate-y-1/2 w-24 h-24 text-error flex items-center justify-center hover:text-error/80 transition-colors cursor-pointer"
            aria-label="Remove field"
          >
            <MaterialIcon iconName="cancel" size="md" />
          </button>
        )}

        {/* Error Message */}
        {errorMessage && (
          <FormFieldSupportingText id={errorId} leadingIconName={errorLeadingIconName} isError>
            {errorMessage}
          </FormFieldSupportingText>
        )}
      </div>
    );
  },
);
